import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

function ShieldLogo({ className = "h-10 w-10" }) {
    return (
        <svg viewBox="0 0 40 44" className={className} fill="none" aria-hidden="true">
            <defs>
                <linearGradient id="fpShield" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5A4" />
                    <stop offset="100%" stopColor="#0F766E" />
                </linearGradient>
            </defs>
            <path
                d="M20 2 L36 8 V20 C36 30 28 38 20 42 C12 38 4 30 4 20 V8 Z"
                fill="url(#fpShield)"
                stroke="#D4AF37"
                strokeWidth="1.4"
            />
            <path
                d="M11 18 Q15 14 20 18 T29 18 M11 23 Q15 19 20 23 T29 23 M11 28 Q15 24 20 28 T29 28"
                stroke="#D4AF37"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
            />
        </svg>
    );
}

export default function ForgotPassword() {
    const { sendPasswordResetEmail } = useAuth();
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await sendPasswordResetEmail(email);
            setSent(true);
            toast.success("Reset email sent — check your inbox.");
        } catch (err) {
            toast.error(err.message || "Could not send reset email");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#071A2F] text-white flex flex-col">
            <div className="absolute inset-0 mesh-hero opacity-40 pointer-events-none" />
            <div className="absolute inset-0 grid-pattern-dark opacity-30 pointer-events-none" />

            <Link
                to="/"
                className="relative flex items-center gap-2.5 p-6 sm:p-10 lg:p-14 z-10"
            >
                <ShieldLogo />
                <span className="font-bold text-lg">
                    TenderTrack<span className="gradient-text-teal">AI</span>
                </span>
            </Link>

            <div className="relative flex-1 flex items-center justify-center px-6 pb-10 z-10">
                <div className="w-full max-w-md">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400 hover:text-white mb-6"
                    >
                        <ArrowLeft className="h-3 w-3" />
                        Back to login
                    </Link>

                    {!sent ? (
                        <>
                            <div className="badge-pill border border-teal-500/30 bg-teal-500/10 text-teal-300">
                                <Mail className="h-3 w-3" /> Password reset
                            </div>
                            <h1 className="mt-6 font-serif text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[1.05]">
                                Forgot your <span className="gradient-text-gold">password?</span>
                            </h1>
                            <p className="mt-3 text-slate-400">
                                Enter your email and we&apos;ll send you a secure link to
                                reset it.
                            </p>

                            <form onSubmit={submit} className="mt-8 space-y-5">
                                <div>
                                    <Label
                                        htmlFor="email"
                                        className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300"
                                    >
                                        Email
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@company.in"
                                        className="mt-2 h-12 rounded-xl bg-white/5 border-white/15 placeholder:text-slate-500 text-white focus-visible:ring-teal-500/40 focus-visible:border-teal-500/40"
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="luxury-btn w-full h-12 rounded-2xl text-sm font-bold uppercase tracking-[0.14em] inline-flex items-center justify-center gap-2 disabled:opacity-70"
                                    data-testid="forgot-submit-btn"
                                >
                                    {loading ? "Sending…" : "Send Reset Link"}
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            </form>
                        </>
                    ) : (
                        <div className="text-center">
                            <div className="mx-auto h-16 w-16 rounded-full bg-teal-500/15 border border-teal-500/30 flex items-center justify-center">
                                <CheckCircle2 className="h-8 w-8 text-teal-300" />
                            </div>
                            <h1 className="mt-6 font-serif text-3xl font-bold uppercase leading-[1.05]">
                                Check your <span className="gradient-text-teal">email</span>
                            </h1>
                            <p className="mt-3 text-slate-400">
                                We&apos;ve sent a password-reset link to{" "}
                                <span className="text-white font-mono">{email}</span>. It
                                expires in 1 hour.
                            </p>
                            <Link to="/login" className="mt-8 inline-block">
                                <button className="luxury-btn-gold px-6 h-12 rounded-2xl text-sm font-bold uppercase tracking-[0.14em]">
                                    Back to Login
                                </button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
